import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * V8.1 · Institutional Quality panel.
 * Wires the P2b-1 endpoints (already backend-ready) to a self-contained UI
 * on the Assessment page. Feature-flag: `institutional_confidence_ui`.
 */
export default function InstitutionalQualityPanel({ assessmentId }) {
  const [confidence, setConfidence] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [qa, setQa] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    setLoading(true);
    Promise.allSettled([
      api.get(`/assessments/${assessmentId}/institutional-confidence`),
      api.get(`/assessments/${assessmentId}/coverage`),
      api.get(`/assessments/${assessmentId}/conflicts`),
      api.get(`/assessments/${assessmentId}/qa`),
    ]).then(([c, cov, cf, q]) => {
      if (!ok) return;
      if (c.status === "fulfilled") setConfidence(c.value.data);
      if (cov.status === "fulfilled") setCoverage(cov.value.data);
      if (cf.status === "fulfilled") setConflicts(cf.value.data);
      if (q.status === "fulfilled") setQa(q.value.data);
      setLoading(false);
    });
    return () => { ok = false; };
  }, [assessmentId]);

  if (loading) {
    return (
      <div data-testid="iq-loading" className="text-sm text-slate-500">
        Loading institutional quality signals…
      </div>
    );
  }

  return (
    <div data-testid="institutional-quality-panel" className="space-y-6">
      <ConfidenceCard c={confidence} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CoverageCard cov={coverage} />
        <ConflictsCard cf={conflicts} />
        <QaCard q={qa} />
      </div>
    </div>
  );
}

function bandColor(band) {
  return {
    HIGH: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    MODERATE: "bg-amber-50 text-amber-800 ring-amber-200",
    LIMITED: "bg-orange-50 text-orange-800 ring-orange-200",
    INSUFFICIENT: "bg-red-50 text-red-800 ring-red-200",
  }[band] || "bg-slate-100 text-slate-700 ring-slate-200";
}

function ConfidenceCard({ c }) {
  if (!c) return null;
  const dims = c.dimensions || {};
  return (
    <div data-testid="iq-confidence" className="rounded-2xl border border-slate-200 p-5 bg-white">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Institutional Confidence
          </div>
          <div className="text-2xl font-serif text-slate-900 mt-1">
            {c.overall?.toFixed?.(1) ?? "—"} / 100
          </div>
        </div>
        <span
          data-testid="iq-confidence-band"
          className={`text-xs uppercase font-medium px-3 py-1 rounded-full ring-1 ${bandColor(c.status)}`}
        >
          {c.status}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
        {Object.entries(dims).map(([key, val]) => (
          <div key={key} data-testid={`iq-dim-${key}`} className="border border-slate-100 rounded-lg p-3">
            <div className="text-[11px] uppercase text-slate-500">{key.replace(/_/g, " ")}</div>
            <div className="text-lg font-medium text-slate-800">{val.score}</div>
            <div className="text-[11px] text-slate-500 leading-snug">{val.formula}</div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-400 mt-4">
        Engine {c.engine_version} · computed {c.calculated_at?.slice(0, 19)}Z
      </div>
    </div>
  );
}

function CoverageCard({ cov }) {
  if (!cov) return null;
  return (
    <div data-testid="iq-coverage" className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="text-xs uppercase tracking-wide text-slate-500">Coverage</div>
      <div className="text-2xl font-serif text-slate-900 mt-1">{cov.primary_pct ?? 0}%</div>
      <div className="text-xs text-slate-500 mt-1">
        Primary-source backing across critical claims
      </div>
    </div>
  );
}

function ConflictsCard({ cf }) {
  if (!cf) return null;
  const n = cf.conflicts?.length ?? 0;
  return (
    <div data-testid="iq-conflicts" className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="text-xs uppercase tracking-wide text-slate-500">Conflicts</div>
      <div className={`text-2xl font-serif mt-1 ${n === 0 ? "text-emerald-700" : "text-amber-700"}`}>
        {n}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        Numeric divergences between sources on the same metric
      </div>
    </div>
  );
}

function QaCard({ q }) {
  if (!q) return null;
  const critical = (q.issues || []).filter((i) => i.severity === "critical").length;
  return (
    <div data-testid="iq-qa" className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="text-xs uppercase tracking-wide text-slate-500">QA</div>
      <div className={`text-lg font-medium mt-1 ${q.status === "PASS" ? "text-emerald-700" : q.status === "FAIL" ? "text-red-700" : "text-amber-700"}`}>
        {q.status}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {q.issues?.length ?? 0} issue(s) · {critical} critical
      </div>
    </div>
  );
}
